import { useMemo } from 'react';
import { AppConfig } from '@config';
import { hasOwnProperty, HttpRequest, VALIDATION_REG_EXP } from '@endurancewarranty/lib';
import { useAccessToken } from '@hooks/auth';
import { CustomValidationError } from '@lib/errors/CustomValidationError';
import { impersonationAtom } from '@states/impersonation/impersonationAtom';
import { useJotaiValue } from '@states/jotai/hooks';
import { contactSelector } from '@states/softphone/contactSelector';
import type { TCustomValidationException } from '@app/_components/error/CustomValidationException';
import type { TBody, TPayloadValue } from '@lib/types/THttpRequest';
type HttpRequestFunctions = {
  // Opting to put these functions behind a namespace. I think
  // the context here matters and functions with short names
  // like 'get' and 'delete' can be misleading.
  http: {
    get: (endpoint?: string, query?: Maybe<Record<string, TPayloadValue>>) => HttpRequest;
    post: (endpoint: string, body?: TBody, query?: Record<string, TPayloadValue>) => HttpRequest;
    put: (endpoint: string, body?: Maybe<Record<string, TPayloadValue>>) => HttpRequest;
    patch: (endpoint: string, body?: TBody) => HttpRequest;
    delete: (endpoint: string, query?: Maybe<Record<string, TPayloadValue>>, body?: Maybe<Record<string, TPayloadValue>>) => HttpRequest;
  };
};
export function gatewayErrorInterceptor(_response: Response, body: unknown): Maybe<Error> {
  if (body == null || !hasOwnProperty(body, 'errorType')) {
    return null;
  }
  return new CustomValidationError(body as TCustomValidationException);
}
export function useHttp(): HttpRequestFunctions {
  const contact = useJotaiValue(contactSelector);
  const impersonation = useJotaiValue(impersonationAtom);
  const {
    getAccessToken,
    refreshAndGetAccessToken
  } = useAccessToken();
  const headers = useMemo(() => {
    const headersImpl: Record<string, string> = {};
    if (impersonation.azureAdId != null) {
      headersImpl['x-impersonation-azureadid'] = impersonation.azureAdId;
    }
    if (contact?.contactId != null && contact?.contactMasterId != null) {
      headersImpl['x-contact-id'] = contact.contactId;
      headersImpl['x-contact-master-id'] = contact.contactMasterId;
      if (contact?.contactPhoneNumber != null && contact?.contactPhoneNumber.match(VALIDATION_REG_EXP.phoneNumber) && contact?.transferType == null // The contact phone # on a transfer is an Endurance # due to how InContact handles transfers
      ) {
        headersImpl['x-contact-phone-number'] = contact.contactPhoneNumber;
      }
    }
    return headersImpl;
  }, [contact.contactId, contact.contactMasterId, contact.contactPhoneNumber, contact.transferType, impersonation.azureAdId]);
  const createRequest = (): HttpRequest => new HttpRequest({
    baseUrl: AppConfig.gatewayUrl,
    tokenTask: getAccessToken,
    onUnauthorized: async () => {
      await refreshAndGetAccessToken();
    },
    errorInterceptor: gatewayErrorInterceptor,
    defaultTimeoutMs: AppConfig.defaultApiTimeout
  }).setHeaders(headers);
  return {
    http: {
      get: (endpoint?: string, query?: Maybe<Record<string, TPayloadValue>>) => createRequest().to(endpoint ?? '').get(query),
      post: (endpoint_0: string, body?: TBody, query_0?: Record<string, TPayloadValue>) => {
        const request = createRequest().to(endpoint_0).post(body ?? undefined);
        return query_0 != null ? request.setQuery(query_0) : request;
      },
      put: (endpoint_1: string, body_0?: Maybe<Record<string, TPayloadValue>>) => createRequest().to(endpoint_1).put(body_0 ?? undefined),
      patch: (endpoint_2: string, body_1?: TBody) => createRequest().to(endpoint_2).patch(body_1 ?? undefined),
      delete: (endpoint_3: string, query_1?: Maybe<Record<string, TPayloadValue>>, body_2?: Maybe<Record<string, TPayloadValue>>) => createRequest().to(endpoint_3).delete(query_1, body_2 ?? undefined)
    }
  };
}